import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';

export default function EmailConfirmation() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('Verificando seu email...');
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        // Supabase automatically handles the email confirmation
        // when the user clicks the link with the token
        const { data: { session }, error } = await supabase?.auth?.getSession();

        if (error) {
          setStatus('error');
          setMessage('Erro ao confirmar email. Por favor, tente novamente.');
          console.error('Email confirmation error:', error);
          
          // Redirect to login after error
          setTimeout(() => {
            navigate('/authentication-hub');
          }, 3000);
          return;
        }

        // If we have a session, the email was confirmed successfully
        if (session) {
          setStatus('success');
          setMessage('Email confirmado com sucesso!');
          
          // Start countdown
          const timer = setInterval(() => {
            setCountdown((prev) => {
              if (prev <= 1) {
                clearInterval(timer);
                navigate('/authentication-hub');
                return 0;
              }
              return prev - 1;
            });
          }, 3000);

          return () => clearInterval(timer);
        } else {
          // No session but no error - might need to sign in
          setStatus('success');
          setMessage('Email confirmado! Redirecionando para o login...');
          
          setTimeout(() => {
            navigate('/authentication-hub');
          }, 3000);
        }
      } catch (error) {
        setStatus('error');
        setMessage('Erro ao verificar confirmação. Redirecionando...');
        console.error('Verification error:', error);
        
        setTimeout(() => {
          navigate('/authentication-hub');
        }, 3000);
      }
    };

    verifyEmail();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        {status === 'verifying' && (
          <>
            <div className="flex justify-center mb-6">
              <Loader2 className="w-16 h-16 text-blue-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Verificando Email
            </h1>
            <p className="text-gray-600">
              {message}
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="flex justify-center mb-6">
              <CheckCircle className="w-16 h-16 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Email Confirmado!
            </h1>
            <p className="text-gray-600 mb-6">
              {message}
            </p>
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-blue-700 font-medium">
                Redirecionando para o login em {countdown} segundo{countdown !== 1 ? 's' : ''}...
              </p>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-3xl">⚠️</span>
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-2">
              Erro na Confirmação
            </h1>
            <p className="text-gray-600 mb-6">
              {message}
            </p>
            <div className="bg-red-50 rounded-lg p-4">
              <p className="text-red-700 text-sm">
                Você será redirecionado para a página de login.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}